import { sessionCredentialStorageKey, type SessionStorageLike } from '../client/url.js';
import { rememberMatchDeckMode } from '../decks/matchFlow.js';
import {
  parseChallengeStartResult,
  parseChallengeTrackId,
  type ChallengeStageNumber,
  type ChallengeStartResult,
} from './api.js';

export type ChallengePageRoute = Readonly<{ kind: 'list' }>;
export interface ChallengeMatchContext {
  readonly version: 1;
  readonly trackId: string;
  readonly stage: ChallengeStageNumber;
}

/** Challenge UI deliberately owns one exact route; match URLs remain the existing root route. */
export function parseChallengePageRoute(pathname: string): ChallengePageRoute | undefined {
  return pathname === '/challenges' ? { kind: 'list' } : undefined;
}

export function challengeMatchPath(session: ChallengeStartResult): string {
  const parsed = parseChallengeStartResult(session);
  const params = new URLSearchParams({ room: parsed.roomId, playerId: parsed.playerId });
  return `/?${params.toString()}`;
}

export function challengeMatchContextStorageKey(roomId: string, playerId: string): string {
  return `cards:challenge-context:v1:${encodeURIComponent(roomId)}:${encodeURIComponent(playerId)}`;
}

function parseChallengeMatchContext(value: unknown): ChallengeMatchContext {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('challenge context must be an object');
  }
  const object = value as Record<string, unknown>;
  const keys = Object.keys(object);
  if (keys.length !== 3 || !keys.every((key) => ['version', 'trackId', 'stage'].includes(key))
    || object.version !== 1
    || (object.stage !== 1 && object.stage !== 2 && object.stage !== 3)) {
    throw new Error('invalid challenge context');
  }
  return Object.freeze({
    version: 1,
    trackId: parseChallengeTrackId(object.trackId),
    stage: object.stage,
  });
}

export function readChallengeMatchContext(
  storage: SessionStorageLike,
  roomId: string,
  playerId: string,
): ChallengeMatchContext | undefined {
  const key = challengeMatchContextStorageKey(roomId, playerId);
  const raw = storage.getItem(key);
  if (raw === null) return undefined;
  try {
    return parseChallengeMatchContext(JSON.parse(raw) as unknown);
  } catch {
    storage.removeItem(key);
    return undefined;
  }
}

/**
 * Persist an already-issued reconnect bearer before navigation. The secret is
 * never placed in history or the URL, and write/read verification fails closed.
 */
export function storeChallengeReconnectCredential(
  storage: SessionStorageLike,
  session: ChallengeStartResult,
  context: Readonly<{ trackId: string; stage: ChallengeStageNumber }>,
): void {
  const parsed = parseChallengeStartResult(session);
  const seat = { room: parsed.roomId, playerId: parsed.playerId };
  const key = sessionCredentialStorageKey(seat);
  const serialized = JSON.stringify({
    version: 1,
    token: parsed.reconnectToken,
    acknowledged: true,
  });
  const contextKey = challengeMatchContextStorageKey(parsed.roomId, parsed.playerId);
  const serializedContext = JSON.stringify(parseChallengeMatchContext({ version: 1, ...context }));
  try {
    storage.setItem(key, serialized);
    if (storage.getItem(key) !== serialized) throw new Error('read-back mismatch');
    storage.setItem(contextKey, serializedContext);
    if (storage.getItem(contextKey) !== serializedContext) throw new Error('context read-back mismatch');
    rememberMatchDeckMode(storage, seat, 'challenge');
  } catch {
    throw new Error('Браузер не может сохранить ключ challenge-сессии.');
  }
}
